import React from 'react';

/**
 * Catches any uncaught render/lifecycle error in the child tree so the whole
 * app never shows a blank white page. The user sees a friendly message and
 * can either retry the render or reload the page.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] caught render error:', error, info?.componentStack);
  }

  handleRetry = () => this.setState({ error: null });
  handleReload = () => window.location.reload();

  render() {
    if (!this.state.error) return this.props.children;
    const message = this.state.error?.message || 'Algo salió mal';
    return (
      <div
        data-testid="app-error-boundary"
        className="min-h-screen bg-background flex items-center justify-center px-6 py-12"
      >
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center border border-slate-100">
          <div className="w-16 h-16 mx-auto rounded-full bg-red-50 flex items-center justify-center mb-4 text-3xl">
            😕
          </div>
          <h1
            className="text-2xl font-bold text-[#1E3A5F] mb-2"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Hubo un error
          </h1>
          <p className="text-sm text-slate-600 mb-1">
            La pantalla no cargó bien. Nada de tu información se perdió.
          </p>
          <p className="text-xs text-slate-400 mb-6 break-words">{message}</p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <button
              data-testid="err-retry-btn"
              onClick={this.handleRetry}
              className="px-5 py-2 bg-white border-2 border-[#1E3A5F] text-[#1E3A5F] rounded-full font-semibold hover:bg-[#1E3A5F] hover:text-white transition-all"
            >
              Volver a intentar
            </button>
            <button
              data-testid="err-reload-btn"
              onClick={this.handleReload}
              className="px-5 py-2 bg-[#1E3A5F] text-white rounded-full font-semibold hover:bg-[#152B47] transition-all"
            >
              Recargar la app
            </button>
          </div>
        </div>
      </div>
    );
  }
}
